import React, { useCallback, useEffect, useMemo, useState } from "react"
import { useNavigate } from "react-router-dom"
import dayjs from "dayjs"
import { Bounce, toast } from "react-toastify"
import DashboardCard from "../../../components/DashboardComponents/DashboardCard"
import ListingTabs from "../../../components/Listing/ListingTabs"
import SearchInput from "../../../components/Listing/SearchInput"
import FilterDropdown, {
  toSingleFilterValue,
} from "../../../components/Listing/FilterDropdown"
import Pagination from "../../../components/Listing/Pagination"
import CustomButton from "../../../components/OnboardingComponents/CustomButton"
import CustomIconButton from "../../../components/OnboardingComponents/CustomIconButton"
import EmptyState from "../../../components/Common/EmptyState"
import TruncatedText from "../../../components/Common/TruncatedText"
import DeleteConfirmation from "../../../components/Common/DeleteConfirmation"
import { ICONS } from "../../../constants/icons"
import { del, get } from "../../../api/axios"
import endpoints from "../../../api/endpoints"
import { toastApiError } from "../../../helpers/apiError"
import useAdminPermission from "../../../hooks/useAdminPermission"
import { ADMIN_ROLE_DETAIL_NESTED_VIEW_MODULES } from "../../../config/adminPermissionRegistry"
import {
  mapAdminRoleRow,
  mapRolesSummaryKpi,
  ROLES_VIEW_TABS,
} from "./adminRolesData"

const PAGE_SIZE = 15

const STATUS_LABEL = {
  active: "Active",
  inactive: "Inactive",
}

const formatDate = value => {
  if (!value) return "--"
  const parsed = dayjs(value)
  return parsed.isValid() ? parsed.format("MMM D, YYYY") : "--"
}

/**
 * Admin Roles & Permissions — GET /api/v1/admin/roles/get-all
 */
const AdminRoles = () => {
  const navigate = useNavigate()
  const { can, canViewAll } = useAdminPermission()
  const canCreate = can("roles", "create")
  const canEdit = can("roles", "edit")
  const canDelete = can("roles", "delete")
  const canViewDetail = canViewAll(ADMIN_ROLE_DETAIL_NESTED_VIEW_MODULES)
  const activeTab = "roles"
  const [search, setSearch] = useState("")
  const [debouncedSearch, setDebouncedSearch] = useState("")
  const [typeFilter, setTypeFilter] = useState("")
  const [statusFilter, setStatusFilter] = useState("")
  const [currentPage, setCurrentPage] = useState(1)
  const [rows, setRows] = useState([])
  const [totalItems, setTotalItems] = useState(0)
  const [pageSize, setPageSize] = useState(PAGE_SIZE)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)
  const [kpiCards, setKpiCards] = useState(() => mapRolesSummaryKpi())
  const [isKpiLoading, setIsKpiLoading] = useState(true)
  const [roleToDelete, setRoleToDelete] = useState(null)
  const [isDeleting, setIsDeleting] = useState(false)

  useEffect(() => {
    document.title = "Roles & Permissions | AMOK . EARTH Admin"
  }, [])

  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search.trim()), 300)
    return () => clearTimeout(timer)
  }, [search])

  useEffect(() => {
    setCurrentPage(1)
  }, [debouncedSearch, typeFilter, statusFilter])

  useEffect(() => {
    let cancelled = false
    const fetchSummary = async () => {
      setIsKpiLoading(true)
      try {
        const response = await get(endpoints.getAdminRolesSummary)
        if (cancelled) return
        if (response?.data?.success) {
          setKpiCards(mapRolesSummaryKpi(response.data.result || {}))
        } else {
          toastApiError(response, "Failed to load roles summary")
        }
      } catch (err) {
        if (!cancelled) {
          toastApiError(err, "Failed to load roles summary")
        }
      } finally {
        if (!cancelled) setIsKpiLoading(false)
      }
    }
    fetchSummary()
    return () => {
      cancelled = true
    }
  }, [])

  const fetchRoles = useCallback(async () => {
    setIsLoading(true)
    setError(null)
    try {
      const params = {
        page: currentPage,
        page_size: PAGE_SIZE,
        "with[]": ["permissions", "cities"],
      }
      if (debouncedSearch) params.search = debouncedSearch

      const response = await get(endpoints.getAdminRoles, { params })
      if (response?.data?.success) {
        const result = response.data.result || {}
        const list = Array.isArray(result.data) ? result.data : []
        let mapped = list.map(mapAdminRoleRow)

        if (typeFilter) {
          mapped = mapped.filter(row => row.type === typeFilter)
        }
        if (statusFilter) {
          mapped = mapped.filter(row => row.status === statusFilter)
        }

        setRows(mapped)
        setTotalItems(result.total ?? mapped.length)
        setPageSize(result.per_page || PAGE_SIZE)

        const totalPages = Math.max(
          1,
          Math.ceil(
            (result.total || mapped.length) / (result.per_page || PAGE_SIZE),
          ),
        )
        if (currentPage > totalPages) setCurrentPage(totalPages)
      } else {
        const message = toastApiError(response, "Failed to load roles")
        setError(message)
        setRows([])
        setTotalItems(0)
      }
    } catch (err) {
      const message = toastApiError(err, "Failed to load roles")
      setError(message)
      setRows([])
      setTotalItems(0)
    } finally {
      setIsLoading(false)
    }
  }, [currentPage, debouncedSearch, statusFilter, typeFilter])

  useEffect(() => {
    fetchRoles()
  }, [fetchRoles])

  const handleCreate = () => {
    navigate("/admin/roles/create")
  }

  const handleViewDetail = row => {
    navigate(`/admin/roles/${row.id}`)
  }

  const handleEdit = row => {
    navigate(`/admin/roles/${row.id}/edit`)
  }

  const handleDelete = row => {
    setRoleToDelete(row)
  }

  const closeDeleteModal = () => {
    if (isDeleting) return
    setRoleToDelete(null)
  }

  const handleDeleteConfirm = async () => {
    if (!roleToDelete?.id || isDeleting) return

    setIsDeleting(true)
    try {
      const response = await del(endpoints.deleteAdminRole(roleToDelete.id), {
        skipErrorToast: true,
      })

      if (
        !response ||
        response.status >= 400 ||
        response?.data?.success === false
      ) {
        toastApiError(response, "Failed to delete role")
        return
      }

      toast.success(
        response?.data?.message || `"${roleToDelete.name}" deleted.`,
        {
          position: "bottom-center",
          theme: "colored",
          transition: Bounce,
        },
      )
      setRoleToDelete(null)
      await fetchRoles()

      try {
        const summaryResponse = await get(endpoints.getAdminRolesSummary)
        if (summaryResponse?.data?.success) {
          setKpiCards(mapRolesSummaryKpi(summaryResponse.data.result || {}))
        }
      } catch {
        // list refresh already succeeded
      }
    } catch (err) {
      toastApiError(err, "Failed to delete role")
    } finally {
      setIsDeleting(false)
    }
  }

  const skeletonRows = useMemo(
    () => Array.from({ length: 6 }, (_, index) => `skeleton-${index}`),
    [],
  )

  return (
    <div className="admin-page admin-roles">
      <div className="admin-roles__kpi-grid admin-roles__kpi-grid--three">
        {kpiCards.map(card => (
          <DashboardCard
            key={card.key}
            title={card.title}
            tone={card.tone}
            className="admin-roles__kpi-card"
            fullHeight
          >
            <p className="admin-roles__kpi-value">
              {isKpiLoading ? (
                <span className="admin-roles__skeleton admin-roles__skeleton--kpi" />
              ) : (
                card.value
              )}
            </p>
          </DashboardCard>
        ))}
      </div>

      <div className="admin-roles__toolbar">
        <div className="admin-roles__toolbar-left">
          <ListingTabs
            items={ROLES_VIEW_TABS}
            activeKey={activeTab}
            onChange={() => {}}
          />
        </div>
        <div className="admin-roles__controls">
          <SearchInput
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search by title..."
            iconSrc={ICONS.callFormalSearchStatus}
            className="admin-roles__search"
          />
          <FilterDropdown
            label="Filter"
            filters={[
              {
                key: "type",
                type: "select",
                label: "Role Type",
                options: [
                  { value: "system", label: "System" },
                  { value: "custom", label: "Custom" },
                ],
                value: typeFilter,
                defaultValue: "",
                onChange: next => {
                  setTypeFilter(toSingleFilterValue(next))
                },
              },
              {
                key: "status",
                type: "select",
                label: "Status",
                options: [
                  { value: "active", label: "Active" },
                  { value: "inactive", label: "Inactive" },
                ],
                value: statusFilter,
                defaultValue: "",
                onChange: next => {
                  setStatusFilter(toSingleFilterValue(next))
                },
              },
            ]}
          />
          {canCreate ? (
            <CustomButton
              variant="primary"
              size="md"
              type="button"
              trailingIconSrc={ICONS.plus}
              trailingIconAlt=""
              onClick={handleCreate}
            >
              Create Role
            </CustomButton>
          ) : null}
        </div>
      </div>

      <div className="admin-roles__table-wrapper">
        <div
          className="admin-roles__table"
          role="table"
          aria-busy={isLoading || undefined}
        >
          <div
            className="admin-roles__table-row admin-roles__table-row--head"
            role="row"
          >
            <div role="columnheader">Role Name</div>
            <div role="columnheader">Description</div>
            <div role="columnheader">Users</div>
            <div role="columnheader">Status</div>
            <div role="columnheader">Last Updated</div>
            <div role="columnheader" className="admin-roles__actions-head">
              Action
            </div>
          </div>

          {isLoading &&
            skeletonRows.map(key => (
              <div
                key={key}
                className="admin-roles__table-row admin-roles__table-row--skeleton"
                role="row"
              >
                <div className="admin-roles__skeleton" />
                <div className="admin-roles__skeleton" />
                <div className="admin-roles__skeleton" />
                <div className="admin-roles__skeleton" />
                <div className="admin-roles__skeleton" />
                <div className="admin-roles__skeleton" />
              </div>
            ))}

          {!isLoading &&
            rows.map(row => (
              <div key={row.id} className="admin-roles__table-row" role="row">
                <TruncatedText
                  as="div"
                  className="admin-roles__name-cell"
                  role="cell"
                >
                  {row.name}
                </TruncatedText>
                <TruncatedText
                  as="div"
                  className="admin-roles__cell admin-roles__cell--description"
                  role="cell"
                >
                  {row.description}
                </TruncatedText>
                <div className="admin-roles__users-cell" role="cell">
                  <img
                    src={ICONS.user}
                    alt=""
                    width={14}
                    height={14}
                    className="admin-roles__users-icon"
                  />
                  <span>
                    {row.users < 10
                      ? String(row.users).padStart(2, "0")
                      : row.users.toLocaleString()}
                  </span>
                </div>
                <div className="admin-roles__status-cell" role="cell">
                  <span
                    className={`admin-roles__status admin-roles__status--${row.status}`}
                  >
                    <span
                      className="admin-roles__status-dot"
                      aria-hidden="true"
                    />
                    {STATUS_LABEL[row.status] || row.status}
                  </span>
                </div>
                <TruncatedText
                  as="div"
                  className="admin-roles__cell"
                  role="cell"
                >
                  {formatDate(row.lastUpdated)}
                </TruncatedText>
                <div className="admin-roles__actions" role="cell">
                  {canViewDetail ? (
                    <CustomButton
                      variant="transparent"
                      size="sm"
                      type="button"
                      trailingIconSrc={ICONS.arrowRight12}
                      trailingIconAlt=""
                      onClick={() => handleViewDetail(row)}
                    >
                      View Detail
                    </CustomButton>
                  ) : null}
                  {canEdit ? (
                    <CustomIconButton
                      iconSrc={ICONS.edit}
                      label={`Edit ${row.name}`}
                      size="sm"
                      onClick={() => handleEdit(row)}
                    />
                  ) : null}
                  {canDelete ? (
                    <CustomIconButton
                      iconSrc={ICONS.trash}
                      label={`Delete ${row.name}`}
                      tone="danger"
                      size="sm"
                      onClick={() => handleDelete(row)}
                    />
                  ) : null}
                </div>
              </div>
            ))}

          {!isLoading && !error && rows.length === 0 && (
            <div className="admin-roles__empty">
              <EmptyState
                title="No roles found"
                description="Try adjusting your search or filters."
              />
            </div>
          )}

          {!isLoading && error && rows.length === 0 && (
            <div className="admin-roles__empty">
              <EmptyState
                title="Unable to load roles"
                description={error}
                actionText="Try again"
                onActionClick={fetchRoles}
              />
            </div>
          )}
        </div>
      </div>

      <Pagination
        currentPage={currentPage}
        totalItems={totalItems}
        pageSize={pageSize}
        onPageChange={setCurrentPage}
        itemLabel="Roles"
        prevIconSrc={ICONS.chevronLeft}
        nextIconSrc={ICONS.chevronRight}
        className="admin-roles__pagination"
      />

      <DeleteConfirmation
        isOpen={Boolean(roleToDelete)}
        onCancel={closeDeleteModal}
        onConfirm={handleDeleteConfirm}
        title="Delete Role"
        message={
          roleToDelete
            ? `Are you sure you want to delete “${roleToDelete.name}”? This action cannot be undone.`
            : ""
        }
        cancelText="Cancel"
        confirmText="Delete Role"
        confirmLoadingText="Deleting..."
        isDeleting={isDeleting}
      />
    </div>
  )
}

export default AdminRoles
